import { datadogRum } from '@datadog/browser-rum';
import { toast } from '@side/pantry';
import { useCallback } from 'react';
import { useMutateInteractionLists } from '@/common/hooks/useMutateInteractionLists';
import { InteractionType } from '@/common/types/interaction';
import { isCommunityListingStatus } from '@/components/SideBar/useInteractionStatus/useInteractionStatus.helpers';
import {
  useAddPageBannerToState,
  useClearPageBannerById,
} from '@/state/navigation';
import {
  StatusChangeBannerId,
  StatusChangedBanner,
} from '@/views/Coversheet/components/PageBanner/StatusChangedBanner';
import { coreApi } from '$api-clients/clients';
import type { CoreSchema } from '$api-clients/generated';
import { useCoreMutate } from '$api-clients/hooks';

type ListingPatchBody =
  CoreSchema.paths['/listings/{listingId}']['patch']['requestBody']['content']['application/json'];
type OfferPatchBody =
  CoreSchema.paths['/offers/{offerId}']['patch']['requestBody']['content']['application/json'];
type LeasePatchBody = Extract<
  CoreSchema.paths['/interactions/{interactionId}']['patch']['requestBody']['content']['application/json'],
  { type: 'lease' }
>['interaction'];

// The status is already valid for the current type (the dropdown is built from
// `getTransactionStatusList`), so the casts are safe. `market` is only needed
// for the community-listing check, which only applies to listings.
async function patchInteractionStatus(
  type: InteractionType,
  id: string,
  status: string,
  mutateCore: ReturnType<typeof useCoreMutate>,
) {
  if (type === InteractionType.listing) {
    const { data, error } = await coreApi.PATCH('/listings/{listingId}', {
      params: { path: { listingId: id } },
      body: { status: status as ListingPatchBody['status'] },
    });
    if (data !== undefined) {
      void mutateCore(
        ['/listings'],
        (current) =>
          current && {
            ...current,
            results: current.results.map((row) =>
              row.id === id ? { ...row, status: data.status } : row,
            ),
          },
        { revalidate: false },
      );
    }
    return { error, market: data?.property?.state ?? '' };
  }

  if (type === InteractionType.offer) {
    const { data, error } = await coreApi.PATCH('/offers/{offerId}', {
      params: { path: { offerId: id } },
      body: { status: status as OfferPatchBody['status'] },
    });
    if (data !== undefined) {
      void mutateCore(
        ['/offers'],
        (current) =>
          current && {
            ...current,
            results: current.results.map((row) =>
              row.id === id ? { ...row, status: data.status } : row,
            ),
          },
        { revalidate: false },
      );
    }
    return { error, market: '' };
  }

  if (type === InteractionType.lease) {
    const { data, error } = await coreApi.PATCH(
      '/interactions/{interactionId}',
      {
        params: { path: { interactionId: id } },
        body: {
          type: 'lease',
          interaction: { status: status as LeasePatchBody['status'] },
        },
      },
    );
    const lease = data?.interaction;
    if (lease !== undefined) {
      void mutateCore(
        ['/interactions'],
        (current) =>
          current && {
            ...current,
            results: current.results.map((row) =>
              row.id === id && row.interaction.type === 'lease'
                ? {
                    ...row,
                    interaction: {
                      ...row.interaction,
                      status: status as NonNullable<LeasePatchBody['status']>,
                    },
                  }
                : row,
            ),
          },
        { revalidate: false },
      );
    }
    return { error, market: '' };
  }

  throw new Error(`Unsupported interaction type: ${type}`);
}

/**
 * `showOnInsideListings` is the listing's opt-in state as the caller has it
 * loaded, before the write. core-service opts a listing out of the Listing Feed
 * during the status PATCH, so the response cannot report the value the agent
 * is about to lose.
 */
export function useUpdateInteractionStatus(
  type: InteractionType,
  id: string,
  showOnInsideListings?: boolean,
) {
  const mutateCore = useCoreMutate();
  const mutateInteractionLists = useMutateInteractionLists();
  const addBanner = useAddPageBannerToState();
  const clearBanner = useClearPageBannerById(id);

  return useCallback(
    async (status: string) => {
      try {
        const { error, market } = await patchInteractionStatus(
          type,
          id,
          status,
          mutateCore,
        );
        if (error !== undefined) {
          throw new Error(typeof error === 'string' ? error : error.message);
        }

        // The row's status was already patched into the list caches from the
        // PATCH response; the background revalidation fixes list membership,
        // since active filters may now exclude (or include) the row.
        void mutateInteractionLists(`${type}`);

        if (type !== InteractionType.listing) {
          return true;
        }

        const isValidCommunityListingStatus = isCommunityListingStatus({
          status,
          market,
        });

        if (isValidCommunityListingStatus) {
          clearBanner(StatusChangeBannerId);
          return true;
        }

        if (showOnInsideListings) {
          addBanner(
            {
              id: StatusChangeBannerId,
              banner: (
                <StatusChangedBanner
                  interactionId={id}
                  interactionStatus={status}
                  market={market}
                />
              ),
            },
            id,
          );
        }

        return true;
      } catch (err) {
        datadogRum.addError('Failed to update interaction status', {
          err,
          type,
          id,
          status,
        });
        toast('error', 'Failed to update status', {
          showCloseButton: true,
        });
        return false;
      }
    },
    [
      type,
      id,
      showOnInsideListings,
      mutateCore,
      mutateInteractionLists,
      addBanner,
      clearBanner,
    ],
  );
}
